import {
  Box,
  ButtonBase,
  Divider,
  List,
  ListItem,
  ListItemText,
  Popover,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ArrowDownward, ArrowUpward, Check, UnfoldMore } from '@material-ui/icons';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import { useRef, useState } from 'react';
import { LeadsDashboardGridStore } from '../../stores/leadsDashboardGridStore';
import {
  getDefaultSortBy,
  SORT_DIRECTION_OPTIONS,
} from './leadsDashboardSortOptions';

const useStyles = makeStyles(theme => ({
  label: {
    color: '#5C6BA8',
    marginRight: theme.spacing(1.5),
    whiteSpace: 'nowrap',
  },
  trigger: {
    minWidth: 220,
    height: 40,
    padding: theme.spacing(0, 1.5, 0, 2),
    border: '1px solid #C5CCF2',
    borderRadius: 999,
    background: '#fff',
    justifyContent: 'space-between',
    fontSize: 15,
    color: theme.palette.text.primary,
    '&:hover': {
      borderColor: '#8E9BE0',
    },
  },
  triggerOpen: {
    background: '#EAEDFB',
  },
  unfoldIcon: {
    fontSize: 18,
    color: '#5C6BA8',
    marginLeft: theme.spacing(1),
  },
  paper: {
    marginTop: theme.spacing(0.5),
    minWidth: 240,
    border: '1px solid #C5CCF2',
    borderRadius: 16,
    boxShadow: '0 8px 24px rgba(19, 35, 63, 0.12)',
  },
  directionGroup: {
    display: 'flex',
    margin: theme.spacing(1.5),
    padding: 3,
    borderRadius: 999,
    background: '#F4F5FB',
  },
  directionOption: {
    flex: 1,
    height: 36,
    borderRadius: 999,
    border: '1px solid transparent',
    fontSize: 14,
    color: '#5C6BA8',
    '& svg': {
      fontSize: 16,
      marginRight: theme.spacing(0.75),
    },
  },
  directionOptionSelected: {
    background: '#fff',
    borderColor: '#C5CCF2',
    color: theme.palette.text.primary,
  },
  sectionTitle: {
    display: 'block',
    padding: theme.spacing(1.5, 2, 0.5),
    fontWeight: 600,
    letterSpacing: 0.8,
    color: '#5C6BA8',
  },
  optionCheck: {
    fontSize: 18,
    marginLeft: theme.spacing(2),
    color: '#13233F',
  },
}));

export const LeadsDashboardSortBy = observer(
  ({ gridStore }: { gridStore: LeadsDashboardGridStore }) => {
    const classes = useStyles();
    const anchorRef = useRef<HTMLButtonElement>(null);
    const [open, setOpen] = useState(false);
    const { activeSortBy: sortBy, sortDir, sortOptions } = gridStore;
    const defaultSortBy = getDefaultSortBy(sortOptions);

    const selectedOption = sortOptions.find(
      option => option.value === sortBy
    );

    return (
      <Box display="flex" alignItems="center">
        <Typography variant="body2" className={classes.label}>
          Sort by
        </Typography>
        <ButtonBase
          ref={anchorRef}
          className={clsx(classes.trigger, open && classes.triggerOpen)}
          onClick={() => setOpen(true)}
          data-testid="leads-dashboard-sort-by"
        >
          {selectedOption?.label}
          <UnfoldMore className={classes.unfoldIcon} />
        </ButtonBase>
        <Popover
          open={open}
          anchorEl={anchorRef.current}
          onClose={() => setOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          classes={{ paper: classes.paper }}
        >
          <Box className={classes.directionGroup}>
            {SORT_DIRECTION_OPTIONS.map(direction => (
              <ButtonBase
                key={direction.value}
                className={clsx(
                  classes.directionOption,
                  direction.value === sortDir && classes.directionOptionSelected
                )}
                onClick={() => gridStore.setSort(sortBy, direction.value)}
              >
                {direction.value === 'DESC' ? <ArrowDownward /> : <ArrowUpward />}
                {direction.label}
              </ButtonBase>
            ))}
          </Box>
          <Divider />
          <Typography variant="overline" className={classes.sectionTitle}>
            Sort records by
          </Typography>
          <List dense disablePadding>
            {sortOptions.map(option => (
              <ListItem
                key={option.value}
                button
                onClick={() => {
                  gridStore.setSort(option.value, sortDir);
                  setOpen(false);
                }}
              >
                <ListItemText
                  primary={`${option.label}${option.value === defaultSortBy ? ' (Default)' : ''}`}
                />
                {option.value === sortBy && (
                  <Check className={classes.optionCheck} />
                )}
              </ListItem>
            ))}
          </List>
        </Popover>
      </Box>
    );
  }
);
